/**
 * <EmailExportDialog> — modal form that collects recipient/format/note
 * and submits to /api/export/email. View-only: state + submit handler
 * live in the parent hook.
 */
import React from 'react';
import { Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { FORMATS } from './constants';

const EmailExportDialog = ({
  open,
  onOpenChange,
  testIdPrefix,
  emailForm,
  setEmailForm,
  emailing,
  onSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white" data-testid={`${testIdPrefix}-email-dialog`}>
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold text-[#020617]">Email this export</DialogTitle>
        <DialogDescription className="text-[#64748B]">
          Choose a format and we&apos;ll send it as an attachment.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="export-to">Send to</Label>
          <Input
            id="export-to"
            type="email"
            value={emailForm.to}
            onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
            required
            className="mt-1.5"
            data-testid={`${testIdPrefix}-email-to`}
          />
          <p className="text-xs text-[#64748B] mt-1">
            Defaults to your login email — change it if you&apos;d like to send elsewhere.
          </p>
        </div>
        <div>
          <Label>Format</Label>
          <Select value={emailForm.format} onValueChange={(v) => setEmailForm({ ...emailForm, format: v })}>
            <SelectTrigger className="mt-1.5" data-testid={`${testIdPrefix}-email-format`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FORMATS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="export-note">Note (optional)</Label>
          <Textarea
            id="export-note"
            rows={3}
            value={emailForm.note}
            onChange={(e) => setEmailForm({ ...emailForm, note: e.target.value })}
            placeholder="Add any context for the recipient..."
            className="mt-1.5"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            disabled={emailing}
            className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]"
            data-testid={`${testIdPrefix}-email-send`}
          >
            <Mail className="w-4 h-4 mr-2" />
            {emailing ? 'Sending...' : 'Send'}
          </Button>
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
          >
            Cancel
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export default EmailExportDialog;
